import { isFutureWallTime, toAmsterdamInput, toApiWallTime } from './amsterdamTime';

/**
 * Review form for one scan: values stay strings while editing; only fields that differ from
 * the loaded scan are sent. Rules mirror the backend (PATCH / PUT admin/scans/:id).
 */

export const FIELDS = ['fuelOptionId', 'unitPrice', 'volume', 'totalAmount', 'receiptAt', 'receiptNumber'];
/** Approving needs these to be known after the edits (backend: 400 MISSING_FIELDS). */
export const REQUIRED_TO_APPROVE = ['fuelOptionId', 'unitPrice', 'receiptAt', 'receiptNumber'];
export const MATH_TOLERANCE = 0.02;

const NUMBERS = {
  unitPrice: { max: 100, decimals: 3, label: 'Unit price', range: 'more than 0 and at most 100' },
  volume: { max: 500, decimals: 2, label: 'Volume', range: 'more than 0 and at most 500' },
  totalAmount: { max: 10000, decimals: 2, label: 'Total amount', range: 'more than 0 and at most 10,000' },
};
const RECEIPT_NUMBER = /^[A-Z0-9-]{3,40}$/;

export const fieldId = (field) => `scan-${field}`;

const fixed = (v, digits) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '');

/** Ticket number as typed: upper case, no spaces. */
export const normalizeReceiptNumber = (value) => String(value ?? '').toUpperCase().replace(/\s+/g, '');

/** @param {import('./scanSchema').ScanDetail} scan */
export function toForm(scan) {
  return {
    fuelOptionId: scan.fuelOption ? String(scan.fuelOption.id) : '',
    unitPrice: fixed(scan.unitPrice, 3),
    volume: fixed(scan.volume, 2),
    totalAmount: fixed(scan.totalAmount, 2),
    receiptAt: toAmsterdamInput(scan.receiptAt),
    receiptNumber: scan.receiptNumber ?? '',
  };
}

/** "2,429" or "2.429" → 2.429; "" → null; anything else → NaN. */
export function parseNumber(value) {
  const s = String(value ?? '').trim().replace(',', '.');
  if (!s) return null;
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
}

const decimalsOf = (value) => (String(value).trim().replace(',', '.').split('.')[1] ?? '').length;

/**
 * Field errors for the form. With `approve`, the fields approving needs are also required.
 * @returns {Record<string, string>}
 */
export function validateForm(form, { approve = false, initial } = {}) {
  const errors = {};
  for (const [field, rule] of Object.entries(NUMBERS)) {
    const n = parseNumber(form[field]);
    if (n === null) continue;
    if (Number.isNaN(n) || n <= 0 || n > rule.max) errors[field] = `${rule.label} must be ${rule.range}.`;
    else if (decimalsOf(form[field]) > rule.decimals) errors[field] = `Use at most ${rule.decimals} decimals.`;
  }

  if (form.receiptAt) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(form.receiptAt)) errors.receiptAt = 'Enter a date and time.';
    else if (isFutureWallTime(form.receiptAt)) errors.receiptAt = "The receipt time can't be in the future.";
  } else if (initial?.receiptAt) {
    errors.receiptAt = "The receipt time can't be removed.";
  }

  const receiptNumber = normalizeReceiptNumber(form.receiptNumber);
  if (receiptNumber && !RECEIPT_NUMBER.test(receiptNumber)) errors.receiptNumber = 'Use 3–40 letters, digits or -.';
  else if (!receiptNumber && initial?.receiptNumber) errors.receiptNumber = "The ticket number can't be removed.";

  if (approve) {
    for (const field of REQUIRED_TO_APPROVE) {
      if (!errors[field] && !String(form[field] ?? '').trim()) errors[field] = requiredMessage(field);
    }
  }
  return errors;
}

const REQUIRED_LABELS = {
  fuelOptionId: 'Choose the fuel',
  unitPrice: 'Enter the unit price',
  receiptAt: 'Enter the receipt time',
  receiptNumber: 'Enter the ticket number',
  volume: 'Enter the volume',
  totalAmount: 'Enter the total',
};
export const requiredMessage = (field) => `${REQUIRED_LABELS[field] ?? 'This field is required'} to approve.`;

/** First field (in form order) with an error. */
export const firstErrorField = (errors) => FIELDS.find((f) => errors[f]) ?? null;

const sameNumber = (a, b, digits) => (a === null || b === null ? a === b : Math.abs(a - b) < 10 ** -(digits + 1));

/**
 * Only the fields that differ from the loaded scan, as the API wants them: numbers as JSON
 * numbers, cleared numbers as null, receiptAt as Amsterdam wall time without offset.
 * @param {import('./scanSchema').ScanDetail} scan
 */
export function changedFields(form, scan) {
  const changes = {};
  const fuel = form.fuelOptionId ? Number(form.fuelOptionId) : null;
  if (fuel !== (scan.fuelOption?.id ?? null)) changes.fuelOptionId = fuel;

  for (const [field, rule] of Object.entries(NUMBERS)) {
    const n = parseNumber(form[field]);
    if (!sameNumber(Number.isNaN(n) ? null : n, scan[field], rule.decimals)) changes[field] = n;
  }

  const receiptAt = toApiWallTime(form.receiptAt);
  if (receiptAt && receiptAt !== toApiWallTime(toAmsterdamInput(scan.receiptAt))) changes.receiptAt = receiptAt;

  const receiptNumber = normalizeReceiptNumber(form.receiptNumber);
  if (receiptNumber && receiptNumber !== (scan.receiptNumber ?? '')) changes.receiptNumber = receiptNumber;
  return changes;
}

export const isDirty = (form, scan) => Object.keys(changedFields(form, scan)).length > 0;

/**
 * Live "total ÷ volume = price" check from the form values: `null` until all three are
 * numbers > 0. Same ±€0.02 rule as the backend's mathCheck.
 */
export function mathHint(form) {
  const price = parseNumber(form.unitPrice);
  const volume = parseNumber(form.volume);
  const total = parseNumber(form.totalAmount);
  if (![price, volume, total].every((n) => typeof n === 'number' && n > 0)) return null;
  return {
    total,
    volume,
    price,
    expectedUnitPrice: Math.round((total / volume) * 1000) / 1000,
    matches: Math.abs(price * volume - total) <= MATH_TOLERANCE + 1e-9,
  };
}

/** Backend field errors → form fields; anything else → `general`. */
export function mapServerErrors(fieldErrors = []) {
  const errors = {};
  const general = [];
  for (const { field, message } of fieldErrors) {
    if (FIELDS.includes(field)) errors[field] = errors[field] ?? message;
    else general.push(message);
  }
  return { errors, general };
}

/** The OCR value of a field, as a form string (for "OCR: …" hints and "Use OCR value"). */
export function ocrFormValue(scan, field) {
  const ocr = scan.ocr;
  switch (field) {
    case 'fuelOptionId':
      return ocr.fuelOption ? String(ocr.fuelOption.id) : null;
    case 'unitPrice':
      return ocr.unitPrice === null ? null : fixed(ocr.unitPrice, 3);
    case 'volume':
      return ocr.volume === null ? null : fixed(ocr.volume, 2);
    case 'totalAmount':
      return ocr.totalAmount === null ? null : fixed(ocr.totalAmount, 2);
    case 'receiptAt':
      return ocr.receiptAt ? toAmsterdamInput(ocr.receiptAt) : null;
    case 'receiptNumber':
      return typeof ocr.checks?.receiptNumber === 'string' && ocr.checks.receiptNumber ? normalizeReceiptNumber(ocr.checks.receiptNumber) : null;
    default:
      return null;
  }
}

/** True when the form value differs from what OCR read (and OCR read something). */
export function differsFromOcr(form, scan, field) {
  const ocr = ocrFormValue(scan, field);
  if (ocr === null) return false;
  if (field in NUMBERS) return !sameNumber(parseNumber(form[field]), parseNumber(ocr), NUMBERS[field].decimals);
  if (field === 'receiptAt') return toApiWallTime(form.receiptAt) !== toApiWallTime(ocr);
  if (field === 'receiptNumber') return normalizeReceiptNumber(form.receiptNumber) !== ocr;
  return form[field] !== ocr;
}
